import { Component, signal, inject, OnInit, DestroyRef, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Reconciliation } from '../../models/reconciliation';
import { RequestService } from '../request-service/request.service';
import { RoleService } from '../../core/services/role.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { IconSearchComponent } from '../../shared/components/icon-search/icon-search.component';
import { CustomerService } from '../../core/services/customer.service';
import { SkeletonListComponent, SkeletonColumn } from '../../shared/components/skeleton-list/skeleton-list.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SourceTypeService } from '../../core/services/source-type/source-type.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { BatchService } from '../../core/services/batch/batch.service';
import { OperationTypeService } from '../../core/services/operation-type/operation-type.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SourceType } from '../../models/sourceType';
import { Batch } from '../../models/batch';
import { OperationType } from '../../models/operationType';
import { SelectComponent } from '../../shared/components/select/select.component';
import { DateFilterComponent } from '../../shared/components/date-filter/date-filter.component';
import { AlertComponent } from '../../shared/components/alert/alert.component';

import { DataTableComponent, TableCellDirective, TableColumn } from '../../shared/components/data-table';
import { ArchiveButtonComponent } from '../../shared/components/archive-button/archive-button.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { FriendlyDatePipe } from '../../pipes/friendly-date.pipe';
import { DateTooltipDirective } from '../../directives/date-tooltip.directive';
import { InputComponent } from '../../shared/components/input/input.component';

@Component({
  selector: 'app-request-list',
  standalone: true,
  styleUrl: './request-list.component.css',
  imports: [
    RouterModule,
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    IconSearchComponent,
    SelectComponent,
    DateFilterComponent,
    NgSelectModule,
    AlertComponent,
    DataTableComponent,
    TableCellDirective,
    ArchiveButtonComponent,
    StatusBadgeComponent,
    ButtonComponent,
    ConfirmModalComponent,
    FriendlyDatePipe,
    DateTooltipDirective,
    InputComponent
  ],
  templateUrl: './request-list.component.html'
})
export class RequestListComponent {
  get columns(): TableColumn[] {
    const cols: TableColumn[] = [
      { key: 'name', label: 'REQUESTLIST.TABLE.HEADER.REQUEST_NAME', width: this.isAgent ? '30%' : '36%' },
      { key: 'internalSourceName', label: 'REQUESTLIST.TABLE.HEADER.SOURCE_1', width: this.isAgent ? '22%' : '24%' },
      { key: 'createdDate', label: 'REQUESTLIST.TABLE.HEADER.DATE', width: this.isAgent ? '23%' : '24%' },
      { key: 'status', label: 'REQUESTLIST.TABLE.HEADER.STATUS', width: this.isAgent ? '15%' : '16%' },
    ];
    if (this.isAgent) {
      cols.push({ key: 'action', label: 'Action', width: '10%', align: 'center' });
    }
    return cols;
  }

  requestToArchive: any = null;
  isArchivingRequest = false;

  openArchiveModal(request: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.requestToArchive = request;
  }

  closeArchiveModal(): void {
    this.requestToArchive = null;
    this.isArchivingRequest = false;
  }

  confirmArchiveRequest(): void {
    if (!this.requestToArchive) {
      return;
    }
    const id = this.requestToArchive.id;
    this.isArchivingRequest = true;
    this.requestService.archiveRequest(id).subscribe(
      () => {
        this.updateCurrentRequestList(id);
        this.isArchivingRequest = false;
        this.closeArchiveModal();
      },
      (err: any) => {
        this.errorMsg = err.error?.text || err.message || 'Erreur lors de l\'archivage';
        this.isArchivingRequest = false;
        this.closeArchiveModal();
      }
    );
  }

  loading: boolean = false;
  requestList: any = [];
  errorMsg: string = '';
  infoMsg: string = '';
  isAgent: boolean = false;
  selectedCustomer: any = null;
  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalPages = signal<number>(0);
  lastPage = signal<boolean>(false);
  firstPage = signal<boolean>(false);
  skeletonColumns: SkeletonColumn[] = [];
  externalSourcetypes = signal<SourceType[]>([]);
  internalSourcetypes = signal<SourceType[]>([]);
  filteredExternalSourcetypes = signal<SourceType[]>([]);
  filteredInternalSourcetypes = signal<SourceType[]>([]);
  reconciliationBatches = signal<Batch[]>([]);
  operationTypes = signal<any>([]);
  filteredReconciliationBatches = signal<any>([]);
  isLoadingSourceTypes = signal<boolean>(false);
  isLoadingReconciliationBatches = signal<boolean>(false);
  isLoadingOperationTypes = signal<boolean>(false);
  selectedOperationTypes = signal<any>([]);
  selectedReconciliationBatches = signal<any>([]);
  selectedSource1 = signal<any>([]);
  statusList: any = [
    { label: 'REQUESTLIST.STATUS.NEW', code: 'NEW' },
    { label: 'REQUESTLIST.STATUS.INPROGRESS', code: 'INPROGRESS' },
    { label: 'REQUESTLIST.STATUS.ERROR', code: 'ERROR' },
    { label: 'REQUESTLIST.STATUS.DONE', code: 'DONE' }
  ];
  archiveStatusList: any = [
    { label: 'REQUESTLIST.ARCHIVED_STATUS.ARCHIVED', code: true },
    { label: 'REQUESTLIST.ARCHIVED_STATUS.NOT_ARCHIVED', code: false }
  ];
  filterForm!: FormGroup;
  private hadActiveFilters = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private requestService: RequestService,
    private roleService: RoleService,
    protected router: Router,
    private customerService: CustomerService,
    private translate: TranslateService,
    private sourceTypeService: SourceTypeService,
    private operationTypeService: OperationTypeService,
    private batchService: BatchService,
    private destroyRef: DestroyRef
  ) {
  }

  initializeFilterForm() {
    this.filterForm = this.fb.group({
      name: [''],
      dateStart: [''],
      dateEnd: [''],
      source1: [[]],
      status: [[]],
      operationTypeId: [[]],
      reconciliationBatchId: [[]],
      archive: ['']
    });
  }

  patchFilterFormFromQueryParams(params: any) {
    let reconciliationBatchIdValue: number[] = [];
    if (params['reconciliationBatchId']) {
      reconciliationBatchIdValue = Array.isArray(params['reconciliationBatchId'])
        ? params['reconciliationBatchId'].map((id: any) => Number(id))
        : [Number(params['reconciliationBatchId'])];
    }
    let operationTypeIdValue: number[] = [];
    if (params['operationTypeId']) {
      operationTypeIdValue = Array.isArray(params['operationTypeId'])
        ? params['operationTypeId'].map((id: any) => Number(id))
        : [Number(params['operationTypeId'])];
    }
    let source1Value: string[] = [];
    if (params['source1']) {
      source1Value = Array.isArray(params['source1'])
        ? params['source1']
        : [params['source1']];
    }
    let statusValue: string[] = [];
    if (params['status']) {
      statusValue = Array.isArray(params['status'])
        ? params['status']
        : [params['status']];
    }
    let archiveValue = '';
    if (params['archive'] !== undefined && params['archive'] !== null && params['archive'] !== '') {
      archiveValue = String(params['archive']);
    }

    this.filterForm.patchValue({
      name: params['name'] || '',
      dateStart: params['dateStart'] || '',
      dateEnd: params['dateEnd'] || '',
      source1: source1Value,
      status: statusValue,
      operationTypeId: operationTypeIdValue,
      reconciliationBatchId: reconciliationBatchIdValue,
      archive: archiveValue
    }, { emitEvent: false });
    this.hadActiveFilters = this.hasActiveFilters();
  }

  checkResetIfEmpty() {
    if (!this.hasActiveFilters()) {
      if (this.hadActiveFilters) {
        this.resetFilters();
      }
    } else {
      this.hadActiveFilters = true;
    }
  }

  hasActiveFilters(): boolean {
    if (!this.filterForm) return false;
    const val = this.filterForm.value;
    const nameVal = val.name && val.name.trim() !== '';
    const dateStartVal = !!val.dateStart;
    const dateEndVal = !!val.dateEnd;
    const source1Val = Array.isArray(val.source1) ? val.source1.length > 0 : !!val.source1;
    const statusVal = Array.isArray(val.status) ? val.status.length > 0 : !!val.status;
    const opTypeVal = Array.isArray(val.operationTypeId) ? val.operationTypeId.length > 0 : !!val.operationTypeId;
    const batchVal = Array.isArray(val.reconciliationBatchId) ? val.reconciliationBatchId.length > 0 : !!val.reconciliationBatchId;
    const archiveVal = val.archive !== undefined && val.archive !== null && val.archive !== '';
    return !!(nameVal || dateStartVal || dateEndVal || source1Val || statusVal || opTypeVal || batchVal || archiveVal);
  }

  getActiveFilterChips(): { prefix: string; value: string; remove: () => void }[] {
    if (!this.filterForm) return [];
    const val = this.filterForm.value;
    const chips: { prefix: string; value: string; remove: () => void }[] = [];

    if (val.name && val.name.trim() !== '') {
      chips.push({
        prefix: this.translate.instant('REQUESTLIST.FILTERS.SEARCH'),
        value: val.name.trim(),
        remove: () => {
          this.filterForm.patchValue({ name: '' });
          this.applyFilters();
        }
      });
    }

    if (val.status) {
      const statuses = Array.isArray(val.status) ? val.status : [val.status];
      statuses.forEach((stCode: string) => {
        const found = this.statusList.find((s: any) => s.code === stCode);
        const labelText = this.translate.instant(found.label);
        chips.push({
          prefix: this.translate.instant('REQUESTLIST.FILTERS.STATUS') + ':',
          value: labelText,
          remove: () => this.removeFilterItem('status', stCode)
        });
      });
    }

    if (val.operationTypeId) {
      const opIds = Array.isArray(val.operationTypeId) ? val.operationTypeId : [val.operationTypeId];
      opIds.forEach((opId: number) => {
        const found = this.operationTypes().find((op: any) => op.id === opId);
        const labelText = found?.name || '';
        chips.push({
          prefix: this.translate.instant('REQUESTLIST.FILTERS.OPERATION_TYPE') + ':',
          value: labelText,
          remove: () => this.removeFilterItem('operationTypeId', opId)
        });
      });
    }

    if (val.reconciliationBatchId) {
      const batchIds = Array.isArray(val.reconciliationBatchId) ? val.reconciliationBatchId : [val.reconciliationBatchId];
      batchIds.forEach((bId: number) => {
        const found = this.reconciliationBatches().find((b: any) => b.id === bId);
        const labelText = found?.name || '';
        chips.push({
          prefix: this.translate.instant('REQUESTLIST.FILTERS.BATCH') + ': ',
          value: labelText,
          remove: () => this.removeFilterItem('reconciliationBatchId', bId)
        });
      });
    }

    if (val.source1) {
      const sources = Array.isArray(val.source1) ? val.source1 : [val.source1];
      sources.forEach((srcCode: string) => {
        const found = this.externalSourcetypes().find((s: any) => s.code === srcCode);
        const labelText = found ? found.name : srcCode;
        chips.push({
          prefix: "Source :",
          value: labelText,
          remove: () => this.removeFilterItem('source1', srcCode)
        });
      });
    }

    if (val.dateStart || val.dateEnd) {
      let dateStr = '';
      if (val.dateStart && val.dateEnd) {
        dateStr = `${val.dateStart} ${this.translate.instant('REQUESTLIST.FILTERS.TO')} ${val.dateEnd}`;
      } else if (val.dateStart) {
        dateStr = `${this.translate.instant('REQUESTLIST.FILTERS.SINCE')} ${val.dateStart}`;
      } else {
        dateStr = `${this.translate.instant('REQUESTLIST.FILTERS.UNTIL')} ${val.dateEnd}`;
      }
      chips.push({
        prefix: "Date :",
        value: dateStr,
        remove: () => {
          this.filterForm.patchValue({ dateStart: '', dateEnd: '' });
          this.applyFilters();
        }
      });
    }

    if (val.archive !== undefined && val.archive !== null && val.archive !== '') {
      const isArchived = String(val.archive) === 'true';
      const labelText = isArchived ? this.translate.instant('REQUESTLIST.ARCHIVED_STATUS.ARCHIVED') : this.translate.instant('REQUESTLIST.ARCHIVED_STATUS.NOT_ARCHIVED');
      chips.push({
        prefix: this.translate.instant('REQUESTLIST.FILTERS.ARCHIVE_STATUS') + ':',
        value: labelText,
        remove: () => {
          this.filterForm.patchValue({ archive: '' });
          this.applyFilters();
        }
      });
    }

    return chips;
  }

  removeFilterItem(controlName: string, value: any) {
    const current = this.filterForm.get(controlName)?.value;
    if (Array.isArray(current)) {
      const updated = current.filter((v: any) => v !== value);
      this.filterForm.patchValue({ [controlName]: updated.length > 0 ? updated : [] });
    } else {
      this.filterForm.patchValue({ [controlName]: null });
    }
    this.applyFilters();
  }

  ngOnInit(): void {
    this.loading = true;
    this.isAgent = this.roleService.isAgent();
    if (this.isAgent) {
      this.initializeFilterForm();
      this.handleQueryParams();
      this.handleOperationTypeChanges();
      this.handleReconciliationBatchChanges();
      this.handleSource1Changes();
      this.customerService.selectedCustomer$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((customer) => {
        let skipQueryRestore = false;
        if (customer) {
          if (this.selectedCustomer && this.selectedCustomer.id !== customer.id) {
            skipQueryRestore = true;
            this.resetFilters(false);
          }
          this.selectedCustomer = customer;
        }

        if (!skipQueryRestore) {
          const queryOperationTypeIds = this.route.snapshot.queryParams['operationTypeId'];
          let operationTypeIds: number[] = [];
          if (queryOperationTypeIds) {
            operationTypeIds = Array.isArray(queryOperationTypeIds)
              ? queryOperationTypeIds.map((id: any) => Number(id))
              : [Number(queryOperationTypeIds)];
          }

          if (operationTypeIds.length > 0) {
            this.getReconciliationBatchesByOperationTypeId(operationTypeIds);
          } else {
            this.getReconciliationBatchOptions();
          }
        } else {
          this.getReconciliationBatchOptions();
        }

        this.getSourceTypes();
        this.getOperationTypes(skipQueryRestore);
        this.getRequests();
      });
    }
    this.updateSkeletonColumns();
  }

  handleQueryParams() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const pageFromUrl = +params['page'] || 1;
      this.currentPage.set(pageFromUrl);
      this.patchFilterFormFromQueryParams(params);
      const source1 = params['source1'];
      if (source1) {
        const source1List = Array.isArray(source1) ? source1 : [source1];
        const selected = this.externalSourcetypes().filter((source: any) => source1List.includes(source.code));
        this.selectedSource1.set(selected);

        const masterCodes = selected.map((source: any) => source.sourceTypeMasterCode).filter(Boolean);

        if (masterCodes.length > 0) {
          this.filteredInternalSourcetypes.set(
            this.internalSourcetypes().filter(
              (source: any) => masterCodes.includes(source.code)
            )
          );
        } else {
          this.filteredInternalSourcetypes.set(this.internalSourcetypes());
        }
      } else {
        this.selectedSource1.set([]);
        this.filteredInternalSourcetypes.set(this.internalSourcetypes());
      }
    });
  }

  updateSkeletonColumns() {
    this.skeletonColumns = [
      { width: '25%', theme: { 'width': '80%', 'height': '16px' } },
      { width: '17%', theme: { 'width': '70%', 'height': '14px' } },
      { width: '17%', theme: { 'width': '70%', 'height': '14px' } },
      { width: '21%', theme: { 'width': '60%', 'height': '14px' } },
      { width: '10%', theme: { 'width': '80px', 'height': '24px', 'border-radius': '8px' } }
    ];
    if (this.isAgent) {
      this.skeletonColumns.push({
        width: '10%',
        appearance: 'circle',
        className: 'flex justify-center',
        theme: { 'width': '24px', 'height': '24px' }
      });
    }
  }

  updateCurrentRequestList(id: number) {
    this.requestList = this.requestList.filter(
      (request: Reconciliation) => request.id !== id
    );
  }

  getRequestLiMessage(message: string) {
    this.infoMsg = message;
  }

  getRequests() {
    this.loading = true;
    const filters = { ...this.filterForm.value };
    filters.name = filters.name.trim();
    const currentParams = { ...this.route.snapshot.queryParams };
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        currentParams[key] = filters[key];
      } else {
        delete currentParams[key];
      }
    });
    currentParams['page'] = this.currentPage();
    this.router.navigate([], {
      queryParams: currentParams,
    });

    this.requestService.getArchivedRequest(this.selectedCustomer?.id, this.currentPage() - 1, this.pageSize(), filters).subscribe(
      (res: any) => {
        this.requestList = res.content;
        this.totalPages.update(value => res.totalPages);
        this.lastPage.update(value => res.last);
        this.firstPage.update(value => res.first);
        this.loading = false;
      },
      (err: any) => {
        this.errorMsg = err;
        this.loading = false;
      }
    );
  }

  showDetails(request: any) {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    const queryParams: any = { page: this.currentPage() };
    if (this.selectedCustomer) {
      queryParams.customerId = this.selectedCustomer?.id;
    }
    this.router.navigate([`backoffice/requestdetail/${request.id}`], { queryParams, queryParamsHandling: 'merge' });
  }

  updateCurrentPage(page: number) {
    this.currentPage.set(page);
    this.getRequests()
  }

  applyFilters() {
    this.currentPage.set(1);
    this.hadActiveFilters = this.hasActiveFilters();
    this.getRequests();
  }

  resetFilters(loadRequests: boolean = true) {
    this.hadActiveFilters = false;
    this.filterForm.reset({
      name: '',
      dateStart: '',
      dateEnd: '',
      source1: [],
      status: [],
      reconciliationBatchId: [],
      operationTypeId: [],
      archive: '',
    }, { emitEvent: false });
    this.selectedOperationTypes.set([]);
    this.selectedReconciliationBatches.set([]);
    this.selectedSource1.set([]);

    this.getReconciliationBatchOptions();
    this.getSourceTypes();
    if (loadRequests) {
      this.applyFilters();
    }
  }

  getSourceTypes() {
    this.isLoadingSourceTypes.set(true);
    this.sourceTypeService.getSourceTypes(this.selectedCustomer?.id, true).subscribe(
      (res: any) => {
        const external = res.filter((sourceType: any) => sourceType.sourceTypeMasterCode)
        const internal = res.filter((sourceType: any) => !sourceType.sourceTypeMasterCode)

        this.externalSourcetypes.set(external);
        this.internalSourcetypes.set(internal);
        this.filteredExternalSourcetypes.set(external);
        this.filteredInternalSourcetypes.set(internal);
        this.isLoadingSourceTypes.set(false);
        const source1 = this.filterForm.get('source1')?.value;

        if (source1 && source1.length > 0) {
          const source1List = Array.isArray(source1) ? source1 : [source1];
          const selected = external.filter((source: any) => source1List.includes(source.code));
          this.selectedSource1.set(selected);

          const masterCodes = selected.map((source: any) => source.sourceTypeMasterCode).filter(Boolean);

          if (masterCodes.length > 0) {
            this.filteredInternalSourcetypes.set(
              internal.filter(
                (source: any) => masterCodes.includes(source.code)
              )
            );
          } else {
            this.filteredInternalSourcetypes.set(internal);
          }
        }
      },
      (err: any) => {
        this.errorMsg = err;
        this.isLoadingSourceTypes.set(false);
      }
    );
  }

  getOperationTypes(skipQueryRestore = false) {
    this.isLoadingOperationTypes.set(true);
    this.operationTypeService.getAllOperationTypes(this.selectedCustomer?.id, true).subscribe(
      (res: any) => {
        this.operationTypes.set(res);
        this.isLoadingOperationTypes.set(false);

        if (!skipQueryRestore) {
          const queryOpTypeIds = this.route.snapshot.queryParams['operationTypeId'];
          if (queryOpTypeIds) {
            const opTypeIds = Array.isArray(queryOpTypeIds)
              ? queryOpTypeIds.map((id: any) => Number(id))
              : [Number(queryOpTypeIds)];
            const selected = res.filter((op: any) => opTypeIds.includes(op.id));
            this.selectedOperationTypes.set(selected);
          }
        }
      },
      (err: any) => {
        this.isLoadingOperationTypes.set(false);
        this.errorMsg = err;
      }
    );
  }

  getReconciliationBatchesByOperationTypeId(operationTypeIds: number[]) {
    this.isLoadingReconciliationBatches.set(true);
    this.batchService.getBatchesByOperationType(operationTypeIds, this.selectedCustomer?.id).subscribe({
      next: (res: any) => {
        this.handleReconciliationBatchesResponse(res);
        this.isLoadingReconciliationBatches.set(false);
      },
      error: (err: any) => {
        this.errorMsg = err;
        this.isLoadingReconciliationBatches.set(false);
      }
    }
    );
  }

  getReconciliationBatchOptions() {
    this.isLoadingReconciliationBatches.set(true);
    this.batchService.getReconciliationBatchOptions(this.selectedCustomer?.id).subscribe({
      next: (res: any) => {
        this.handleReconciliationBatchesResponse(res);
        this.isLoadingReconciliationBatches.set(false);
      },
      error: (err: any) => {
        this.errorMsg = err;
        this.isLoadingReconciliationBatches.set(false);
      }
    });
  }

  private handleReconciliationBatchesResponse(res: any): void {
    const batches = res?.sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    );
    this.reconciliationBatches.set(batches);

    let batchIds: number[] = [];
    const queryBatchId = this.route.snapshot.queryParams['reconciliationBatchId'];
    if (queryBatchId) {
      batchIds = Array.isArray(queryBatchId) ? queryBatchId.map((id: any) => Number(id)) : [Number(queryBatchId)];
    }

    const firstBatches = batches.slice(0, 5);

    batchIds.forEach((batchId: number) => {
      const selectedBatch = batches.find(
        (batch: any) => batch.id === batchId
      );
      if (
        selectedBatch &&
        !firstBatches.some((batch: any) => batch.id === selectedBatch.id)
      ) {
        firstBatches.push(selectedBatch);
      }
    });

    this.filteredReconciliationBatches.set(firstBatches);

    if (batchIds.length > 0) {
      const selected = batches.filter((batch: any) => batchIds.includes(batch.id));
      this.selectedReconciliationBatches.set(selected);
      this.filterForm.patchValue({ reconciliationBatchId: batchIds }, { emitEvent: false });
      this.getExternalSourceAndInternalSourceBatch(batchIds);
    }
  }

  onSearchBatch(search: string) {
    const batches = this.reconciliationBatches();

    if (!search || search.trim() === '') {
      this.filteredReconciliationBatches.set(batches.sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      ).slice(0, 5));
      return;
    }

    const filtered = batches.filter((batch: any) =>
      batch.name?.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 5);

    this.filteredReconciliationBatches.set(filtered);
  }

  handleSource1Changes() {
    this.filterForm.get('source1')?.valueChanges.subscribe((value: any) => {

      if (value && value.length > 0) {
        const selected = this.externalSourcetypes().filter((source: any) => value.includes(source.code));
        this.selectedSource1.set(selected);

        const masterCodes = selected.map((source: any) => source.sourceTypeMasterCode).filter(Boolean);

        if (masterCodes.length > 0) {
          this.filteredInternalSourcetypes.set(
            this.internalSourcetypes().filter(
              (source: any) => masterCodes.includes(source.code)
            )
          );
        } else {
          this.filteredInternalSourcetypes.set(this.internalSourcetypes());
        }
      } else {
        this.selectedSource1.set([]);
        this.filteredInternalSourcetypes.set(
          this.internalSourcetypes()
        );
      }
    });
  }

  handleOperationTypeChanges() {
    this.filterForm.get('operationTypeId')?.valueChanges.subscribe((value: any) => {
      this.filterForm.patchValue({ reconciliationBatchId: null }, { emitEvent: false });
      this.selectedReconciliationBatches.set([]);
      this.getSourceTypes();
      if (value && value.length > 0) {
        const selected = this.operationTypes().filter((operationType: { id: number; }) => value?.includes(operationType.id));
        this.selectedOperationTypes.set(selected);
        this.getReconciliationBatchesByOperationTypeId(value);
      } else {
        this.selectedOperationTypes.set([]);
        this.getOperationTypes();
        this.getReconciliationBatchOptions();
      }

      const currentParams = {
        ...this.route.snapshot.queryParams
      };

      if (value && value.length > 0) {
        currentParams['operationTypeId'] = value;
        delete currentParams['reconciliationBatchId'];
        delete currentParams['source1'];
        this.resetSources();
      } else {
        delete currentParams['operationTypeId'];
        delete currentParams['reconciliationBatchId'];
      }
    });
  }

  handleReconciliationBatchChanges() {
    this.filterForm.get('reconciliationBatchId')?.valueChanges.subscribe((batchIds: any) => {
      const currentParams = {
        ...this.route.snapshot.queryParams
      };

      if (batchIds && batchIds.length > 0) {
        const selected = this.reconciliationBatches().filter((batch: { id: number; }) => batchIds?.includes(batch.id));
        this.selectedReconciliationBatches.set(selected);

        delete currentParams['source1'];
        this.resetSources();
        this.getExternalSourceAndInternalSourceBatch(batchIds);
        currentParams['reconciliationBatchId'] = batchIds;
      } else {
        this.selectedReconciliationBatches.set([]);

        delete currentParams['reconciliationBatchId'];
        delete currentParams['source1'];
        this.resetSources();
        this.getSourceTypes();
      }
    });
  }

  getExternalSourceAndInternalSourceBatch(batchIds: number[]) {
    this.batchService.getExternalSourceAndInternalSourceBatch(batchIds).subscribe(
      (res: any) => {
        const external = res.sourceTypeExtern || [];
        const internal = res.sourceTypeMaster || [];

        const uniqueExternal = external.filter((obj: any, index: number, arr: any[]) => arr.findIndex((t: any) => t.code === obj.code) === index);
        const uniqueInternal = internal.filter((obj: any, index: number, arr: any[]) => arr.findIndex((t: any) => t.code === obj.code) === index);

        this.externalSourcetypes.set(uniqueExternal);
        this.internalSourcetypes.set(uniqueInternal);

        this.filteredExternalSourcetypes.set(uniqueExternal);
        this.filteredInternalSourcetypes.set(uniqueInternal);
      },
      (err: any) => {
        this.errorMsg = err;
      }
    )
  }

  private resetSources(): void {
    this.filterForm.patchValue({ source1: null }, { emitEvent: false });
    this.selectedSource1.set([]);
  }

  removeSource1(item: SourceType): void {
    const currentValues = this.filterForm.get('source1')?.value ?? [];
    const newValues = currentValues.filter((code: string) => code !== item.code);
    this.selectedSource1.update((items: any[]) =>
      items.filter((i: any) => i.code !== item.code)
    );
    this.filterForm.patchValue({
      source1: newValues.length > 0 ? newValues : null
    });
  }

  private removeSelectedItem(
    controlName: 'operationTypeId' | 'reconciliationBatchId',
    signal: WritableSignal<{ id: number }[]>,
    itemId: number
  ): void {
    const currentValues = this.filterForm.get(controlName)?.value ?? [];

    signal.update(items =>
      items.filter(i => i.id !== itemId)
    );

    this.filterForm.patchValue({
      [controlName]: currentValues.filter((id: number) => id !== itemId)
    });
  }

  removeOperationType(item: OperationType): void {
    this.removeSelectedItem(
      'operationTypeId',
      this.selectedOperationTypes,
      item.id
    );
  }

  removeReconciliationBatch(item: Batch): void {
    this.removeSelectedItem(
      'reconciliationBatchId',
      this.selectedReconciliationBatches,
      item.id
    );
  }
}
